/**
 * Locale-aware number/date helpers. Centralized so the numeral system
 * (latn or arab) is honored in one place.
 *
 * EVERY user-facing number, date and time must go through here (or through
 * lib/money.ts for money) — never `Intl.*` / `toLocaleString` at the call site.
 * A bare Intl call resolves the `ar` locale to the `arab` numbering system, so
 * it silently prints ٠١٢٣ as soon as the UI language is Arabic. These helpers
 * default to Latin digits; Arabic-Indic is opt-in via `numberingSystem`.
 */
export type NumberingSystem = 'latn' | 'arab';

export interface IntlContext {
  locale: string;
  numberingSystem?: NumberingSystem;
  timeZone?: string;
}

export function formatNumber(
  value: number | string,
  ctx: IntlContext,
  options?: Intl.NumberFormatOptions,
): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat(ctx.locale, {
    ...options,
    numberingSystem: ctx.numberingSystem ?? 'latn',
  }).format(n);
}

/**
 * Compact number for chart axes / KPI tiles ("12.5K", "1.2M"). Same rounding
 * as a plain Intl compact format — this only fixes the digits.
 */
export function formatCompact(
  value: number | string,
  ctx: IntlContext,
  maximumFractionDigits = 1,
): string {
  return formatNumber(value, ctx, { notation: 'compact', maximumFractionDigits });
}

// Intl writes an Arabic or Kurdish date for a right-to-left line: "02/09/2026"
// comes with a RIGHT-TO-LEFT MARK after each number, "2 سبتمبر 2026" is in RTL
// order. The admin pages are left-to-right in every language (DirectionScope),
// and on an LTR line those marks scramble the date into "022026/09/". Wrapped
// in a first-strong isolate it lays out as its own run, the way it reads on an
// RTL line, wherever it lands. A Latin-script date has no RTL character and
// comes back untouched. Escapes, not literals: every one of these is invisible.
const RTL_TEXT = /[\u0590-\u08FF\u200F\u061C\uFB1D-\uFDFF\uFE70-\uFEFC]/;
function isolateRtl(text: string): string {
  return RTL_TEXT.test(text) ? `\u2068${text}\u2069` : text;
}

/**
 * `options`, when given, REPLACES the default style (they can't be merged —
 * Intl throws when dateStyle is combined with individual date components).
 */
export function formatDate(
  date: Date | string,
  ctx: IntlContext,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return isolateRtl(new Intl.DateTimeFormat(ctx.locale, {
    timeZone: ctx.timeZone,
    ...(options ?? { dateStyle: 'medium' }),
    numberingSystem: ctx.numberingSystem ?? 'latn',
  }).format(d));
}

/** See formatDate for how `options` interacts with the default style. */
export function formatDateTime(
  date: Date | string,
  ctx: IntlContext,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return isolateRtl(new Intl.DateTimeFormat(ctx.locale, {
    timeZone: ctx.timeZone,
    ...(options ?? { dateStyle: 'medium', timeStyle: 'short' }),
    numberingSystem: ctx.numberingSystem ?? 'latn',
  }).format(d));
}

/** Time of day only — defaults to a 2-digit HH:MM clock. */
export function formatTime(
  date: Date | string,
  ctx: IntlContext,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return isolateRtl(new Intl.DateTimeFormat(ctx.locale, {
    timeZone: ctx.timeZone,
    ...(options ?? { hour: '2-digit', minute: '2-digit' }),
    numberingSystem: ctx.numberingSystem ?? 'latn',
  }).format(d));
}

export function formatRelative(date: Date | string, ctx: IntlContext): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = d.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  // numberingSystem is supported at runtime but missing from RelativeTimeFormatOptions in lib.es5.d.ts.
  const rtf = new Intl.RelativeTimeFormat(ctx.locale, {
    numeric: 'auto',
    ...({ numberingSystem: ctx.numberingSystem ?? 'latn' } as object),
  });
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return rtf.format(diffH, 'hour');
  const diffD = Math.round(diffH / 24);
  return rtf.format(diffD, 'day');
}
